import { useEffect, useMemo, useState } from 'react'

import type { DeliveryCity, DeliveryPartnersKeys, DeliveryPointItem, PvzTypes } from '@/entities/order/types/delivery'
import { useRegion } from '@/entities/regions'

import { useDebounce } from '@/shared/lib/hooks/new/useDebounce'

import { useGetPvzPointsQuery, useLazyFindDeliveryCityQuery, useLazyGetPointInfoQuery } from '../api/pvz-api'
import { getFilteredPoints } from '../lib/getFilteredPoints'

const SPB_CENTER = [59.964782100421665, 30.345255046874975]

export const usePvz = () => {
	const { currentRegion } = useRegion()
	const { REGION_NAME, CITY_FIAS_ID, GPS_N, GPS_S } = currentRegion

	const [fiasId, setFiasId] = useState(CITY_FIAS_ID)

	const { data, isLoading } = useGetPvzPointsQuery({ fiasId })

	const [activePoint, setActivePoint] = useState<DeliveryPointItem>(null)
	const [activeType, setActiveType] = useState<DeliveryPartnersKeys>(null)
	const [cityList, setCityList] = useState<DeliveryCity[]>([])
	const [cityName, setCityName] = useState(REGION_NAME || null)
	const [pvzType, changePvzType] = useState<PvzTypes>(null)
	const [centerCoords, setCenterCoords] = useState(GPS_N ? [GPS_N, GPS_S] : SPB_CENTER)
	const [getDeliveryInfo, { currentData: deliveryPointInfo, isLoading: isDeliveryInfoLoading }] =
		useLazyGetPointInfoQuery()
	const [fetchCityData] = useLazyFindDeliveryCityQuery()

	const points = useMemo(
		() => (!!data ? getFilteredPoints(data?.POINTS, activeType, pvzType) : []),
		[data, activeType, pvzType]
	)
	const debouncedAddressValue = useDebounce(cityName, 500)

	useEffect(() => {
		fetchCityList(debouncedAddressValue)
	}, [debouncedAddressValue])

	const selectActivePoint = async (point: DeliveryPointItem) => {
		setActivePoint(null)
		setActivePoint(point)

		await getDeliveryInfo({ pvzCode: point.DELIVERY_PVZ_CODE, pvzPartner: point.DELIVERY_PVZ_PARTNER })
	}

	const removeActivePoint = () => {
		setActivePoint(null)
	}

	const fetchCityList = async (cityName: string) => {
		if (cityName?.length < 3) return
		const cityData = await fetchCityData({ cityName })
		if (cityData) {
			setCityList(cityData?.data)
		}
	}

	const changeCityName = async (cityName: string) => {
		setCityName(cityName)
	}

	const selectCity = (cityName: string) => {
		const currentCity = cityList?.find(({ NAME }) => NAME === cityName)

		if (!currentCity) return

		setCityName(currentCity.NAME)
		setFiasId(currentCity.DELIVERY_FIAS_ID)
		setCenterCoords([currentCity?.GPS_N, currentCity?.GPS_S])
	}

	return {
		points,
		pointType: data?.POINT_TYPE,
		partners: data?.PARTNERS,
		activePoint,
		deliveryPointInfo,
		cityName,
		centerCoords,
		isLoading,
		isDeliveryInfoLoading,
		cityList,
		selectCity,
		setActiveType,
		changePvzType,
		removeActivePoint,
		changeCityName,
		setActivePoint: selectActivePoint
	}
}
